import { React, useContext } from "react";
import { css, cx, keyframes } from "@emotion/css";
import { LocationContext } from "../../reducers/LocationContext";

// ─── Animations ──────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popUp = keyframes`
  from { opacity: 0; transform: translateY(32px) scale(0.94); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const overlayStyles = css`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(10, 4, 0, 0.72);
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.22s ease-out;
`;

const cardStyles = css`
  position: relative;
  width: 100%;
  max-width: 440px;
  background: linear-gradient(160deg, #fff9f4 0%, #fdfbf7 100%);
  border-radius: 24px;
  border: 1.5px solid rgba(241, 102, 2, 0.2);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
  animation: ${popUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  font-family: 'Inter', 'Outfit', sans-serif;
`;

/* Top decorative gradient strip */
const topBarStyles = css`
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #f16602 0%, #ff9a3c 60%, #ffd18c 100%);
`;

const closeBtnStyles = css`
  position: absolute;
  top: 14px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(241, 102, 2, 0.08);
  color: #888;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s, transform 0.2s;

  &:hover {
    background: rgba(241, 102, 2, 0.15);
    color: #f16602;
    transform: scale(1.1) rotate(90deg);
  }
`;

const bodyStyles = css`
  padding: 28px 28px 32px;
`;

const iconRingStyles = css`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(241,102,2,0.14) 0%, rgba(255,180,80,0.12) 100%);
  border: 1.5px solid rgba(241, 102, 2, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  font-size: 28px;
`;

const badgeStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(241, 102, 2, 0.1);
  border: 1px solid rgba(241, 102, 2, 0.3);
  color: #f16602;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
`;

const headingStyles = css`
  font-size: 1.45rem;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0 0 8px;
  line-height: 1.2;

  span {
    background: linear-gradient(90deg, #f16602, #ff8c42);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const subtitleStyles = css`
  font-size: 0.9rem;
  color: #6b6b6b;
  margin: 0 0 22px;
  line-height: 1.5;
`;

const dividerStyles = css`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, rgba(241,102,2,0.2), transparent);
  margin-bottom: 20px;
`;

const featureListStyles = css`
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.9rem;
    color: #2a2a2a;
    font-weight: 500;
  }
`;

const checkCircle = css`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f16602, #ff9a3c);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;

  svg {
    width: 11px;
    height: 11px;
    stroke: #fff;
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const actionRowStyles = css`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const laterBtnStyles = css`
  padding: 11px 20px;
  border-radius: 12px;
  border: 1.5px solid rgba(241, 102, 2, 0.25);
  background: transparent;
  color: #666;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  font-family: inherit;

  &:hover {
    background: rgba(241, 102, 2, 0.06);
    color: #f16602;
    border-color: rgba(241, 102, 2, 0.45);
  }
`;

const upgradeBtnStyles = css`
  padding: 11px 24px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #f16602 0%, #d94f00 100%);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(241, 102, 2, 0.38);
  transition: transform 0.18s, box-shadow 0.18s;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(241, 102, 2, 0.48);
  }
  &:active {
    transform: translateY(0);
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
export default function UpgradeModal({ show, onClose }) {
  const { countryCode } = useContext(LocationContext);
  if (!show) return null;

  const handleUpgrade = () => {
    window.location.href = "/subscription";
  };

  return (
    <div className={overlayStyles} onClick={onClose}>
      <div className={cardStyles} onClick={(e) => e.stopPropagation()}>
        {/* Top colour strip */}
        <div className={topBarStyles} />

        {/* Close button */}
        <button className={closeBtnStyles} onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className={bodyStyles}>
          {/* Icon */}
          <div className={iconRingStyles}>⚡</div>

          {/* Badge */}
          <div className={badgeStyles}>Free Tier</div>

          {/* Heading */}
          <h2 className={headingStyles}>
            Unlock <span>Premium</span> Access
          </h2>
          <p className={subtitleStyles}>
            You're currently on the free plan. Upgrade to practice without daily limits and unlock all features.
          </p>

          <div className={dividerStyles} />

          {/* Feature list */}
          <ul className={featureListStyles}>
            <li>
              <span className={checkCircle}>
                <svg viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" /></svg>
              </span>
              Only 1 practice session per day on the free tier
            </li>
            <li>
              <span className={checkCircle}>
                <svg viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" /></svg>
              </span>
              Upgrade for unlimited daily practice sessions
            </li>
            <li>
              <span className={checkCircle}>
                <svg viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" /></svg>
              </span>
              Access all question banks and advanced analytics
            </li>
          </ul>

          {/* Actions */}
          <div className={actionRowStyles}>
            <button className={laterBtnStyles} onClick={onClose}>
              Maybe Later
            </button>
            <button className={upgradeBtnStyles} onClick={handleUpgrade}>
              🚀 Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
